/**
 * SessionLogWorkspace — redesigned session log page.
 * Matches the visual style of ProgressReportWorkspace.
 * Uses date-range-limited fetching to avoid loading unlimited records.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  fetchSessionLogsByDateRange,
  fetchSessionLogsForStudent,
  toDateMaybe,
  calculateRoundedUsedHours,
} from "../tutor/session_logs/sessionLogApi";
import { fetchAllStudents } from "../admin/scheduling/schedulingApi";
import SearchableStudentDropdown from "../admin/dsat/reports/SearchableStudentDropdown";
import { Pagination } from "./ProgressReportWorkspace";
import { calcAvgRating, StarRating } from "./sessionRating";
import AddSessionLogModal from "./AddSessionLogModal";
import { FiPlus } from "react-icons/fi";

import "./ProgressReportWorkspace.css"; // shared design tokens

// ─── helpers ──────────────────────────────────────────────────────────────────

function toDateKey(value) {
  const d = toDateMaybe(value);
  if (!d) return "";
  return [
    d.getFullYear(),
    String(d.getMonth() + 1).padStart(2, "0"),
    String(d.getDate()).padStart(2, "0"),
  ].join("-");
}

function formatDateDisplay(value) {
  if (!value) return "—";
  const d = toDateMaybe(value);
  if (!d) {
    const parts = String(value).split("-");
    if (parts.length === 3) {
      const dt = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
      if (!Number.isNaN(dt.getTime()))
        return dt.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
    }
    return String(value);
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
}

function formatTime(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function sortByDateDesc(logs) {
  return [...logs].sort((a, b) => {
    const aMs = toDateMaybe(a?.sessionStart)?.getTime() || 0;
    const bMs = toDateMaybe(b?.sessionStart)?.getTime() || 0;
    return bMs - aMs;
  });
}

function hwBadgeClass(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "prw-badge--completed";
  if (s.includes("partial")) return "prw-badge--partial";
  if (s === "not done") return "prw-badge--notdone";
  return "prw-badge--neutral";
}

// ─── Session row ─────────────────────────────────────────────────────────────────

function SessionLogRow({ log }) {
  const handleClick = () => {
    if (!log?.id) return;
    localStorage.setItem("sessionLog_" + log.id, JSON.stringify(log));
    localStorage.setItem("tutorSession_" + log.id, JSON.stringify(log));
    window.open(`/session-detail?id=${log.id}`, "_blank");
  };

  const date = formatDateDisplay(log?.sessionStart || log?.sessionDateKey);
  const time = log?.sessionStart
    ? `${formatTime(log.sessionStart)} – ${formatTime(log.sessionEnd)}`
    : "—";
  const hwStatus = String(log?.homeworkStatus || "—");
  const avgRating = calcAvgRating(log);

  const hUsed = Number(log?.usedHours);
  const usedHours = Number.isFinite(hUsed) && hUsed >= 0
    ? hUsed
    : calculateRoundedUsedHours(log?.sessionStart, log?.sessionEnd);

  return (
    <tr className="prw-clickableRow" onClick={handleClick}>
      <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{date}</td>
      <td style={{ whiteSpace: "nowrap", fontSize: 12, color: "#64748b" }}>{time}</td>
      <td>{String(log?.studentName || "—")}</td>
      <td>{String(log?.tutorName || "—")}</td>
      <td>{String(log?.subject || "—")}</td>
      <td>
        <span className={`prw-badge ${hwBadgeClass(hwStatus)}`}>{hwStatus}</span>
      </td>
      <td>
        <StarRating value={avgRating} uid={log?.id} />
      </td>
      <td style={{ fontSize: 11, color: "#94a3b8", whiteSpace: "nowrap" }}>
        {usedHours > 0 ? `${usedHours.toFixed(1)} hrs` : "—"}
      </td>
    </tr>
  );
}

// ─── Main component ─────────────────────────────────────────────────────────────

const SESSIONS_PER_PAGE = 20;
const DEFAULT_RANGE_DAYS = 14;

function defaultDateRange() {
  const end = new Date();
  const start = new Date(end.getTime() - DEFAULT_RANGE_DAYS * 24 * 60 * 60 * 1000);
  return { start: toDateKey(start), end: toDateKey(end) };
}

export default function SessionLogWorkspace({ viewerRole = "admin" }) {
  const isAdminViewer = viewerRole === "admin";
  const [allStudents, setAllStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [selectedStudentId, setSelectedStudentId] = useState("");
  const [addLogOpen, setAddLogOpen] = useState(false);

  const defaults = useMemo(defaultDateRange, []);
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsError, setLogsError] = useState("");

  const [page, setPage] = useState(1);
  const [refreshToken, setRefreshToken] = useState(0);

  // ── Load students
  useEffect(() => {
    let alive = true;
    setStudentsLoading(true);
    fetchAllStudents()
      .then((rows) => {
        if (!alive) return;
        setAllStudents(
          rows.filter((r) => r?.name).map((r) => ({
            uid: r.id,
            name: String(r.name).trim(),
            email: String(r.email || ""),
          }))
        );
      })
      .catch((err) => console.error("Failed to load students for session log", err))
      .finally(() => { if (alive) setStudentsLoading(false); });
    return () => { alive = false; };
  }, []);

  // ── Fetch logs whenever student or date range changes
  const loadLogs = useCallback(async () => {
    setLogsError("");
    setLogsLoading(true);
    setPage(1);

    try {
      if (selectedStudentId) {
        // Student is selected — load all their logs then filter by date
        const student = allStudents.find((s) => s.uid === selectedStudentId);
        if (!student) return;
        const all = await fetchSessionLogsForStudent(student.name, { maxRows: 4000 });
        const startMs = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
        const endMs = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;
        const filtered = sortByDateDesc(
          all.filter((log) => {
            const ms = toDateMaybe(log?.sessionStart)?.getTime();
            if (!ms) return false;
            if (startMs !== null && ms < startMs) return false;
            if (endMs !== null && ms > endMs) return false;
            return true;
          })
        );
        setLogs(filtered);
      } else {
        // No student — load by date range
        const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
        const end = endDate ? new Date(`${endDate}T23:59:59`) : null;
        if (!start || !end) {
          setLogs([]);
          return;
        }
        const rows = await fetchSessionLogsByDateRange({ startDate: start, endDate: end, maxRows: 500 });
        setLogs(sortByDateDesc(rows));
      }
    } catch (err) {
      console.error("Failed to load session logs", err);
      setLogsError("Could not load session logs right now. Please try again.");
      setLogs([]);
    } finally {
      setLogsLoading(false);
    }
  }, [selectedStudentId, startDate, endDate, allStudents]);

  // Auto-load on mount and when dependencies change
  useEffect(() => {
    loadLogs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStudentId, startDate, endDate, refreshToken]);

  // ── Pagination
  const totalPages = Math.max(1, Math.ceil(logs.length / SESSIONS_PER_PAGE));
  const pagedLogs = logs.slice((page - 1) * SESSIONS_PER_PAGE, page * SESSIONS_PER_PAGE);

  const selectedStudent = allStudents.find((s) => s.uid === selectedStudentId) || null;

  const metaText = logsLoading
    ? "Loading…"
    : logsError
    ? "Error loading logs"
    : `${logs.length} session${logs.length !== 1 ? "s" : ""}${selectedStudent ? ` for ${selectedStudent.name}` : ""} — click a row to open detail`;

  return (
    <div className="prw-shell">
      <div className="prw-pageHeader prw-pageHeader--withAction">
        <div>
          <h1>Session Log</h1>
          <p>Search and review tutoring session logs. Click any row to open the full session detail.</p>
        </div>
        {isAdminViewer && (
          <button
            type="button"
            className="prw-generateBtn"
            onClick={() => setAddLogOpen(true)}
          >
            <FiPlus size={14} /> Add Session Log
          </button>
        )}
      </div>

      {/* ── Filter Card ── */}
      <div className="prw-card">
        <div className="prw-cardTitle">Filter Sessions</div>
        <div className="prw-filterRow">
          {/* Student dropdown with clear */}
          <div className="prw-fieldGroup">
            <div className="prw-label">Student</div>
            <div className="prw-dropdownWrap">
              <SearchableStudentDropdown
                students={allStudents}
                selectedId={selectedStudentId}
                onSelect={(uid) => { setSelectedStudentId(uid); setPage(1); }}
                placeholder={studentsLoading ? "Loading students…" : "All students (browse by date range)"}
                disabled={studentsLoading}
              />
              {selectedStudentId && (
                <button
                  type="button"
                  className="prw-clearBtn"
                  title="Clear student filter"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedStudentId("");
                    setPage(1);
                  }}
                >
                  ×
                </button>
              )}
            </div>
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">Start Date</div>
            <input
              type="date"
              className="prw-input"
              value={startDate}
              onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
            />
          </div>

          <div className="prw-fieldGroup">
            <div className="prw-label">End Date</div>
            <input
              type="date"
              className="prw-input"
              value={endDate}
              onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
            />
          </div>

          {/* Spacer for grid alignment */}
          <div className="prw-fieldGroup" aria-hidden="true" />
        </div>
      </div>

      {/* ── Session Logs Table ── */}
      <div className="prw-card">
        <div className="prw-sectionHeader">
          <div className="prw-sectionTitle">Session Logs</div>
          <div className="prw-sectionMeta">{metaText}</div>
        </div>

        {logsError && <div className="prw-errorBox" style={{ marginBottom: 12 }}>{logsError}</div>}

        <div className="prw-tableWrap">
          <table className="prw-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Time</th>
                <th>Student</th>
                <th>Tutor</th>
                <th>Subject</th>
                <th>Homework</th>
                <th>Avg Rating</th>
                <th>Hours</th>
              </tr>
            </thead>
            <tbody>
              {logsLoading && (
                <tr className="prw-emptyRow">
                  <td colSpan={8}>Loading session logs…</td>
                </tr>
              )}
              {!logsLoading && pagedLogs.length === 0 && (
                <tr className="prw-emptyRow">
                  <td colSpan={8}>
                    No session logs found for the selected filters.
                  </td>
                </tr>
              )}
              {!logsLoading && pagedLogs.map((log) => (
                <SessionLogRow key={log.id} log={log} />
              ))}
            </tbody>
          </table>
        </div>

        <Pagination page={page} totalPages={totalPages} onPage={setPage} />
      </div>

      {isAdminViewer && (
        <AddSessionLogModal
          open={addLogOpen}
          onClose={() => {
            setAddLogOpen(false);
            // The admin may have submitted logs in the opened tabs — refresh.
            setRefreshToken((n) => n + 1);
          }}
        />
      )}
    </div>
  );
}
